import { useEffect, useState } from 'react';

import type { LocationRadiusSelectHandle } from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { MobileSearchFilterButton } from '#/components/MobileSearchFilterButton/MobileSearchFilterButton';
import { MobileSearchMenu } from '#/components/MobileSearchMenu/MobileSearchMenu';
import { MobileSearchQueryField } from '#/components/MobileSearchQueryField/MobileSearchQueryField';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { useActiveRefinementCount } from '#/modules/search/hooks/refinements/useActiveRefinementCount';
import { useSearchRefinements } from '#/modules/search/hooks/refinements/useSearchRefinements';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchType } from '#/modules/search/types/SearchType';
import { ViewportBreakpoint } from '#/theme/theme';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackModalView } from '#/utils/analytics/track/trackModalView';

import { MobileSearchDialog } from './MobileSearchDialog';
import { MobileSearchFilterDialog } from './MobileSearchFilterDialog';
import { MobileSearchFilterModalContent } from './MobileSearchFilterModalContent';
import { MobileSearchV2Holder } from './MobileSearchV2.styled';

type Props = {
  defaultSearchType: SearchType;
  isSearchPage: boolean;
  isSearchLandingPage: boolean;
  initialQuery: string;
  searchLocation: SearchLocation | undefined;
  onChangeSearchLocation: (
    newSearchLocation: SearchLocation | undefined,
  ) => boolean;
  radiusOptions: { label: string; value: string }[];
  radiusHandle: LocationRadiusSelectHandle;
  onSearch: (data: {
    listingType: SearchType;
    query: string;
    radius: string;
    triggeredByRemoteLocationChange: boolean;
  }) => void;
  searchOnClear: boolean;
};

export function MobileSearchV2({
  defaultSearchType,
  isSearchPage,
  isSearchLandingPage,
  initialQuery,
  searchLocation,
  onChangeSearchLocation,
  radiusOptions,
  radiusHandle,
  onSearch,
  searchOnClear,
}: Props) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const activeFilterCount = useActiveRefinementCount(defaultSearchType);
  const { setSearchUrlSort } = useSearchPageActions({ isSearchLandingPage });
  const { clearAllRefinements } = useSearchRefinements(defaultSearchType);

  const viewport = useViewportBreakpoint();
  const isDesktop =
    viewport === ViewportBreakpoint.md || viewport === ViewportBreakpoint.lg;

  // TODO(@paprikka): consider not using useEffect and check for !isDesktop && isMenuOpen later in render
  useEffect(() => {
    if (isFilterDialogOpen) {
      trackModalView('Search Filters');
    }
  }, [isFilterDialogOpen]);

  // Mobile search UI is hidden via CSS on desktop, but the dialogs live in
  // React state — close them when the viewport grows to desktop so they don't
  // linger after a resize.
  useEffect(() => {
    if (!isDesktop) return;
    setIsMenuOpen(false);
    setIsFilterDialogOpen(false);
  }, [isDesktop]);

  return (
    <>
      {/* NOTE: ID used in CSS to control header offsets */}
      <MobileSearchV2Holder id="mobile-search-v2">
        <MobileSearchQueryField
          query={initialQuery}
          searchLocation={searchLocation}
          searchType={defaultSearchType}
          radius={radiusHandle.radius}
          showSearchContext={isSearchPage}
          onClick={() => {
            trackClicked('Header Utility CTA - Search');
            setIsMenuOpen(true);
          }}
        />
        <MobileSearchFilterButton
          onClick={() => setIsFilterDialogOpen(true)}
          activeFilterCount={activeFilterCount}
        />
      </MobileSearchV2Holder>

      {isMenuOpen && (
        <MobileSearchDialog onClose={() => setIsMenuOpen(false)}>
          <MobileSearchMenu
            defaultSearchType={defaultSearchType}
            initialQuery={initialQuery}
            searchLocation={searchLocation}
            onChangeSearchLocation={onChangeSearchLocation}
            radiusOptions={radiusOptions}
            radiusHandle={radiusHandle}
            onSearch={onSearch}
            searchOnClear={searchOnClear}
            onClose={() => setIsMenuOpen(false)}
          />
        </MobileSearchDialog>
      )}

      {isFilterDialogOpen && (
        <MobileSearchFilterDialog
          onClose={() => setIsFilterDialogOpen(false)}
          onClearFilters={clearAllRefinements}
        >
          <MobileSearchFilterModalContent
            searchType={defaultSearchType}
            onChangeSort={setSearchUrlSort}
          />
        </MobileSearchFilterDialog>
      )}
    </>
  );
}
